import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Badge,
    Card,
    CardBody,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    List,
    ListItem,
    ListIcon,
    Divider,
    useColorMode,
    SimpleGrid,
} from "@chakra-ui/react";
import { CheckIcon, TimeIcon, StarIcon } from "@chakra-ui/icons";
import { ModuleConfig } from "../../../types/ModuleConfig";
import { getColor } from "../../../brandConfig";

interface ModuleWhatsNewProps {
    moduleConfig: ModuleConfig;
}

/**
 * ModuleWhatsNew - Generic "What's New" component for any module
 *
 * Displays:
 * - Changelog (version history with release notes)
 * - Roadmap (upcoming features organized by priority)
 *
 * Can be used by any module that has changelog/roadmap in their moduleConfig
 */
export const ModuleWhatsNew: React.FC<ModuleWhatsNewProps> = ({ moduleConfig }) => {
    const { colorMode } = useColorMode();
    const [tabIndex, setTabIndex] = useState(0);

    const bgMain = getColor("background.main", colorMode);
    const cardGradientBg = getColor("background.cardGradient", colorMode);
    const cardBorder = getColor("border.darkCard", colorMode);
    const textPrimary = getColor(colorMode === 'light' ? "text.primary" : "text.primaryDark", colorMode);

    const priorityConfig = {
        high: { color: 'red', icon: '🔥', label: 'High Priority' },
        medium: { color: 'orange', icon: '⚡', label: 'Medium Priority' },
        low: { color: 'blue', icon: '💡', label: 'Low Priority' }
    };

    const statusConfig = {
        completed: { color: 'green', icon: CheckIcon },
        'in-progress': { color: 'orange', icon: TimeIcon },
        planned: { color: 'gray', icon: StarIcon }
    };

    return (
        <Box bg={bgMain} minH="100vh">
            <Container maxW="container.xl" py={8}>
                <VStack align="stretch" spacing={6}>
                    {/* Header */}
                    <Box>
                        <HStack spacing={3} mb={2}>
                            <Text fontSize="3xl">{moduleConfig.icon}</Text>
                            <Heading color={textPrimary}>
                                {moduleConfig.name} - What's New
                            </Heading>
                        </HStack>
                        <HStack spacing={3}>
                            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                v{moduleConfig.version}
                            </Badge>
                            <Text color="gray.500">
                                {moduleConfig.description}
                            </Text>
                        </HStack>
                    </Box>

                    {/* Tabs */}
                    <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue">
                        <TabList>
                            <Tab>
                                📋 Changelog
                                {moduleConfig.changelog && (
                                    <Badge ml={2} colorScheme="blue">
                                        {moduleConfig.changelog.length}
                                    </Badge>
                                )}
                            </Tab>
                            <Tab>
                                🗺️ Roadmap
                                {moduleConfig.roadmap && (
                                    <Badge ml={2} colorScheme="purple">
                                        {moduleConfig.roadmap.length}
                                    </Badge>
                                )}
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* CHANGELOG TAB */}
                            <TabPanel px={0}>
                                {moduleConfig.changelog && moduleConfig.changelog.length > 0 ? (
                                    <VStack align="stretch" spacing={6} mt={4}>
                                        {moduleConfig.changelog.map((entry, idx) => (
                                            <Card
                                                key={entry.version}
                                                bg={cardGradientBg}
                                                border="2px"
                                                borderColor={idx === 0 ? "blue.500" : cardBorder}
                                                shadow={idx === 0 ? "lg" : "md"}
                                            >
                                                <CardBody>
                                                    <VStack align="stretch" spacing={4}>
                                                        {/* Version Header */}
                                                        <HStack justify="space-between" flexWrap="wrap">
                                                            <HStack>
                                                                <Heading size="lg" color={textPrimary}>
                                                                    v{entry.version}
                                                                </Heading>
                                                                {idx === 0 && (
                                                                    <Badge colorScheme="green" fontSize="sm">
                                                                        Current
                                                                    </Badge>
                                                                )}
                                                            </HStack>
                                                            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                                                {new Date(entry.releaseDate).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                })}
                                                            </Badge>
                                                        </HStack>

                                                        <Heading size="md" color={textPrimary}>
                                                            {entry.title}
                                                        </Heading>

                                                        <Divider />

                                                        {/* Features */}
                                                        <Box>
                                                            <Heading size="sm" color={textPrimary} mb={3}>
                                                                What's Included:
                                                            </Heading>
                                                            <List spacing={2}>
                                                                {entry.features.map((feature, featureIdx) => (
                                                                    <ListItem key={featureIdx} color={textPrimary}>
                                                                        <Text>{feature}</Text>
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>

                                                        {/* Notes */}
                                                        {entry.notes && (
                                                            <Box
                                                                p={4}
                                                                bg={colorMode === 'light' ? 'blue.50' : 'blue.900'}
                                                                borderRadius="md"
                                                                borderLeft="4px solid"
                                                                borderColor="blue.500"
                                                            >
                                                                <Text fontSize="sm" color={textPrimary} fontStyle="italic">
                                                                    💡 {entry.notes}
                                                                </Text>
                                                            </Box>
                                                        )}
                                                    </VStack>
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </VStack>
                                ) : (
                                    <Card bg={cardGradientBg} border="1px" borderColor={cardBorder}>
                                        <CardBody>
                                            <VStack py={8} spacing={3}>
                                                <Text fontSize="4xl">📋</Text>
                                                <Heading size="md" color={textPrimary}>
                                                    No Version History Yet
                                                </Heading>
                                                <Text color="gray.500" textAlign="center">
                                                    Changelog will appear here as new versions are released.
                                                </Text>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}
                            </TabPanel>

                            {/* ROADMAP TAB */}
                            <TabPanel px={0}>
                                {moduleConfig.roadmap && moduleConfig.roadmap.length > 0 ? (
                                    <VStack align="stretch" spacing={6} mt={4}>
                                        {/* Roadmap Items */}
                                        {moduleConfig.roadmap.map((item, idx) => {
                                            const priority = priorityConfig[item.priority];
                                            const status = statusConfig[item.status];

                                            return (
                                                <Card
                                                    key={idx}
                                                    bg={cardGradientBg}
                                                    border="1px"
                                                    borderColor={cardBorder}
                                                >
                                                    <CardBody>
                                                        <VStack align="stretch" spacing={4}>
                                                            {/* Header */}
                                                            <HStack justify="space-between" flexWrap="wrap">
                                                                <Heading size="md" color={textPrimary}>
                                                                    {item.title}
                                                                </Heading>
                                                                <HStack spacing={2}>
                                                                    <Badge
                                                                        colorScheme={priority.color}
                                                                        fontSize="sm"
                                                                        px={3}
                                                                        py={1}
                                                                    >
                                                                        {priority.icon} {priority.label}
                                                                    </Badge>
                                                                    <Badge
                                                                        colorScheme={status.color}
                                                                        fontSize="sm"
                                                                        px={3}
                                                                        py={1}
                                                                    >
                                                                        {item.status}
                                                                    </Badge>
                                                                    {item.targetVersion && (
                                                                        <Badge
                                                                            variant="outline"
                                                                            colorScheme="purple"
                                                                            fontSize="sm"
                                                                            px={3}
                                                                            py={1}
                                                                        >
                                                                            v{item.targetVersion}
                                                                        </Badge>
                                                                    )}
                                                                </HStack>
                                                            </HStack>

                                                            {/* Description */}
                                                            <Text color="gray.600">
                                                                {item.description}
                                                            </Text>

                                                            <Divider />

                                                            {/* Features */}
                                                            <Box>
                                                                <Heading size="sm" color={textPrimary} mb={3}>
                                                                    Planned Features:
                                                                </Heading>
                                                                <List spacing={2}>
                                                                    {item.features.map((feature, featureIdx) => (
                                                                        <ListItem key={featureIdx}>
                                                                            <HStack align="start">
                                                                                <ListIcon
                                                                                    as={status.icon}
                                                                                    color={`${status.color}.500`}
                                                                                    mt={1}
                                                                                />
                                                                                <Text fontSize="sm" color={textPrimary}>
                                                                                    {feature}
                                                                                </Text>
                                                                            </HStack>
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                            </Box>
                                                        </VStack>
                                                    </CardBody>
                                                </Card>
                                            );
                                        })}
                                    </VStack>
                                ) : (
                                    <Card bg={cardGradientBg} border="1px" borderColor={cardBorder}>
                                        <CardBody>
                                            <VStack py={8} spacing={3}>
                                                <Text fontSize="4xl">🗺️</Text>
                                                <Heading size="md" color={textPrimary}>
                                                    No Roadmap Available
                                                </Heading>
                                                <Text color="gray.500" textAlign="center">
                                                    Future features and plans will appear here.
                                                </Text>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </VStack>
            </Container>
        </Box>
    );
};

export default ModuleWhatsNew;
